import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocale } from '@parentpath/i18n';
import type {
  FAQCategory,
  FAQSubcategory,
  FAQItem,
  FAQSearchResult,
  FAQCategoryWithCount
} from '../types';

// Fetch all active categories with item counts
export function useFAQCategories() {
  const { locale } = useLocale();

  return useQuery({
    queryKey: ['faq-categories', locale],
    queryFn: async (): Promise<FAQCategoryWithCount[]> => {
      // Fetch categories
      const { data: categories, error: catError } = await supabase
        .from('faq_categories')
        .select('*')
        .eq('is_active', true)
        .eq('locale', locale)
        .order('sort_order');

      if (catError) throw catError;

      // Fetch subcategories for all categories
      const { data: subcategories, error: subError } = await supabase
        .from('faq_subcategories')
        .select('*')
        .eq('is_active', true)
        .eq('locale', locale)
        .order('sort_order');

      if (subError) throw subError;

      // Fetch item counts per category
      const { data: items, error: itemError } = await supabase
        .from('faq_items')
        .select('category_id')
        .eq('is_active', true)
        .eq('locale', locale);

      if (itemError) throw itemError;

      // Count items per category
      const itemCounts = items.reduce((acc: Record<string, number>, item) => {
        acc[item.category_id] = (acc[item.category_id] || 0) + 1;
        return acc;
      }, {});

      // Combine data
      return categories.map(cat => ({
        ...cat,
        item_count: itemCounts[cat.id] || 0,
        subcategories: subcategories.filter(sub => sub.category_id === cat.id)
      }));
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Fetch single category with subcategories and items
export function useFAQCategory(slug: string) {
  const { locale } = useLocale();

  return useQuery({
    queryKey: ['faq-category', slug, locale],
    queryFn: async () => {
      // Fetch category
      const { data: category, error: catError } = await supabase
        .from('faq_categories')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .eq('locale', locale)
        .single();

      if (catError) throw catError;

      // Fetch subcategories
      const { data: subcategories, error: subError } = await supabase
        .from('faq_subcategories')
        .select('*')
        .eq('category_id', category.id)
        .eq('is_active', true)
        .eq('locale', locale)
        .order('sort_order');

      if (subError) throw subError;

      // Fetch items
      const { data: items, error: itemError } = await supabase
        .from('faq_items')
        .select('*')
        .eq('category_id', category.id)
        .eq('is_active', true)
        .eq('locale', locale)
        .order('sort_order');

      if (itemError) throw itemError;

      return {
        category: category as FAQCategory,
        subcategories: subcategories as FAQSubcategory[],
        items: items as FAQItem[]
      };
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

// Search FAQ items
export function useFAQSearch(query: string) {
  const { locale } = useLocale();

  return useQuery({
    queryKey: ['faq-search', query, locale],
    queryFn: async (): Promise<FAQSearchResult[]> => {
      if (!query || query.length < 2) return [];

      const { data, error } = await supabase
        .rpc('search_faq', { search_query: query, search_locale: locale });

      if (error) throw error;
      return data || [];
    },
    enabled: query.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
  });
}

// Track FAQ view
export function useTrackFAQView() {
  return useMutation({
    mutationFn: async (faqId: string) => {
      const { error } = await supabase
        .rpc('increment_faq_view', { faq_id: faqId });
      if (error) throw error;
    },
  });
}

// Rate FAQ helpfulness
export function useRateFAQ() {
  return useMutation({
    mutationFn: async ({ faqId, isHelpful }: { faqId: string; isHelpful: boolean }) => {
      const { error } = await supabase
        .rpc('rate_faq_helpfulness', {
          faq_id: faqId,
          is_helpful: isHelpful
        });
      if (error) throw error;
    },
  });
}
